import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
  formatScheduledPaymentDate,
  formatUserMessageText,
  getOrderMutationBlockedMessage,
  getServiceOrderId,
  getServiceOrderNumber,
  isOrderEditable,
  OrderService,
  parseScheduledPaymentDateForInput,
  ProgrammingServiceOrder,
  resolveOrderGraphqlUserMessage,
  shouldRefreshAfterOrderError,
  UpdateOrderResult,
} from 'src/app/entities/order';

/**
 * Data payload injected into the Edit Scheduled Payment Date dialog.
 */
export interface EditScheduledPaymentDateDialogData {
  service: {
    id?: string;
    nombreServicio?: string | null;
    orderId?: string | null;
    orderNumber?: string | number | null;
    estadoOrden?: string | null;
    order?:
      | ProgrammingServiceOrder
      | {
          orderId?: string | null;
          orderNumber?: string | number | null;
          orderStatus?: string | null;
          scheduledPaymentDate?: string | null;
          [key: string]: any;
        }
      | null;
    [key: string]: any;
  };
}

/**
 * Result returned upon dialog dismissal.
 */
export interface EditScheduledPaymentDateDialogResult {
  success: boolean;
  shouldRefresh?: boolean;
  newDate?: Date | null;
  orderNumber?: string | number | null;
  order?: UpdateOrderResult | null;
}

export type EditPaymentDateViewState = 'form' | 'error';

/**
 * Dialog component providing calendar datepicker interface to update scheduled payment date for an Order of Service.
 */
@Component({
  selector: 'app-dialog-edit-fecha-pago-programada',
  standalone: true,
  templateUrl: './dialog-edit-fecha-pago-programada.component.html',
  styleUrls: ['./dialog-edit-fecha-pago-programada.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TablerIconsModule,
  ],
  host: { class: 'edit-payment-date-host' },
})
export class DialogEditFechaPagoProgramadaComponent implements OnInit {
  public viewState: EditPaymentDateViewState = 'form';
  public errorTitleText: string | null = null;
  public errorMessageText: string | null = null;

  public readonly fechaPagoProgramadaInput = new FormControl<Date | null>(
    null,
    [Validators.required],
  );
  public isSubmittingRequest: boolean = false;
  private readonly targetOrderId: string | null;

  /**
   * Initializes the scheduled payment date dialog.
   *
   * @param dialogReference - Reference to manage dialog actions and return results.
   * @param injectedData - Payload containing target service and order details.
   * @param orderService - Service executing GraphQL mutations for orders.
   * @param snackBarService - Material Snackbar service for user feedback.
   */
  constructor(
    public readonly dialogReference: MatDialogRef<
      DialogEditFechaPagoProgramadaComponent,
      EditScheduledPaymentDateDialogResult
    >,
    @Inject(MAT_DIALOG_DATA)
    public readonly injectedData: EditScheduledPaymentDateDialogData,
    private readonly orderService: OrderService,
    private readonly snackBarService: MatSnackBar,
  ) {
    this.targetOrderId = getServiceOrderId(this.injectedData.service);
  }

  /**
   * Initializes input values from injected order payload and evaluates editability.
   *
   * Description:
   *   Extracts existing scheduledPaymentDate, parses into Date object,
   *   and sets input state accordingly.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public ngOnInit(): void {
    const rawPaymentDate =
      this.injectedData.service?.order?.scheduledPaymentDate;
    const parsedDate = parseScheduledPaymentDateForInput(rawPaymentDate);
    if (parsedDate) {
      this.fechaPagoProgramadaInput.setValue(parsedDate);
    }

    if (!this.canEditOrder) {
      this.fechaPagoProgramadaInput.disable();
    }
  }

  /**
   * Evaluates whether the order is eligible for editing (must be in CONFIRMADO status with valid orderId).
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if editable; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get canEditOrder(): boolean {
    return isOrderEditable({
      order: {
        orderId: this.targetOrderId,
        orderStatus:
          this.injectedData.service?.order?.orderStatus ||
          this.injectedData.service?.estadoOrden,
      },
    });
  }

  /**
   * Formats display message explaining why editing is blocked when in non-confirmed status.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Human-readable explanation.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get blockedMessageText(): string {
    const currentStatus =
      this.injectedData.service?.order?.orderStatus ||
      this.injectedData.service?.estadoOrden;
    return getOrderMutationBlockedMessage('editar', currentStatus);
  }

  /**
   * Resolves order number for dialog header display.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string | number - Order number string or placeholder.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get displayedOrderNumber(): string | number {
    return getServiceOrderNumber(this.injectedData.service) || '—';
  }

  /**
   * Submits the updated scheduled payment date to the GraphQL Core API.
   *
   * Description:
   *   Validates input date, formats as YYYY-MM-DD, and triggers updateOrderScheduledPaymentDate mutation.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Catches GraphQL client errors and displays toast or switches to error state.
   */
  public updateFechaPagoProgramada(): void {
    if (this.isSubmittingRequest || !this.canEditOrder) {
      return;
    }

    const selectedDateValue = this.fechaPagoProgramadaInput.value;
    if (this.fechaPagoProgramadaInput.invalid || !selectedDateValue) {
      this.snackBarService.open(
        'Debe ingresar una fecha válida en formato DD/MM/AAAA',
        'Cerrar',
        { duration: 4000 },
      );
      return;
    }

    if (!this.targetOrderId) {
      this.snackBarService.open(
        'El servicio no tiene una OS asociada.',
        'Cerrar',
        { duration: 4000 },
      );
      return;
    }

    this.isSubmittingRequest = true;
    const dateInstance =
      selectedDateValue instanceof Date
        ? selectedDateValue
        : new Date(selectedDateValue);

    this.orderService
      .updateOrderScheduledPaymentDate({
        orderId: this.targetOrderId,
        scheduledPaymentDate: formatScheduledPaymentDate(dateInstance),
      })
      .subscribe({
        next: (mutationResponse) => {
          this.isSubmittingRequest = false;
          this.snackBarService.open(
            'Fecha de pago programada actualizada con éxito.',
            'Cerrar',
            { duration: 3500 },
          );
          this.dialogReference.close({
            success: true,
            shouldRefresh: false,
            newDate: this.fechaPagoProgramadaInput.value,
            orderNumber:
              mutationResponse?.orderNumber ??
              this.injectedData.service?.order?.orderNumber,
            order: mutationResponse,
          });
        },
        error: (httpError: unknown) => {
          this.isSubmittingRequest = false;
          const resolvedMessage = resolveOrderGraphqlUserMessage(
            httpError,
            'No se pudo actualizar la fecha de pago programada.',
          );

          if (shouldRefreshAfterOrderError(httpError)) {
            this.errorTitleText = resolvedMessage.title;
            this.errorMessageText = formatUserMessageText(resolvedMessage);
            this.viewState = 'error';
            this.dialogReference.disableClose = true;
            return;
          }

          this.snackBarService.open(
            `${resolvedMessage.title}: ${formatUserMessageText(resolvedMessage)}`,
            'Cerrar',
            { duration: 5000 },
          );
        },
      });
  }

  /**
   * Closes dialog and instructs parent component to refresh its dataset.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public handleCloseAndRefresh(): void {
    this.dialogReference.disableClose = false;
    this.dialogReference.close({
      success: false,
      shouldRefresh: true,
    });
  }

  /**
   * Cancels editing and dismisses the dialog.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public handleCancel(): void {
    this.dialogReference.close({ success: false, shouldRefresh: false });
  }
}
